/**
 * KAN-834: обновление access-токена по refresh (POST /api/v1/auth/refresh/).
 * Параллельные запросы, получившие 401, ждут одно общее обещание: refresh
 * уходит один раз. Backend ротирует refresh (ROTATE_REFRESH_TOKENS +
 * BLACKLIST_AFTER_ROTATION), поэтому новый refresh сохраняется сразу, иначе
 * второй refresh старым токеном получил бы 401.
 * KAN-925: аккаунтов несколько; refresh, его ожидание и сохранение пары идут
 * по аккаунту, отправившему запрос (userId), а не по активному во вкладке.
 */
import axios from 'axios';
import { findAccount } from '@/lib/accounts';
import { getRefreshToken, updateAuthTokens } from '@/lib/authSession';

export const REFRESH_URL = '/api/v1/auth/refresh/';

/** Запросы, на 401 которых обновлять токен бессмысленно (вход, сам refresh, обмен ссылки). */
const NO_REFRESH_URLS = ['/auth/login', '/auth/refresh', '/auth/app-link'];

export function isAuthRequest(url: string | undefined): boolean {
  return !!url && NO_REFRESH_URLS.some((part) => url.includes(part));
}

interface RefreshResponse {
  access: string;
  refresh?: string;
}

const pending = new Map<string, Promise<string | null>>();

/** Сколько ждать, пока другая вкладка сохранит пару после своего refresh. */
export const OTHER_TAB_WAIT_MS = 1000;

/**
 * Refresh не прошёл. Если за это время другая вкладка того же браузера уже
 * обновила пару этого аккаунта (наш refresh она отправила в чёрный список
 * ротацией), берём её токен вместо выхода. Её ответ мог ещё не дойти: одна
 * короткая пауза.
 */
async function rotatedByOtherTab(userId: string, usedRefresh: string): Promise<string | null> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const current = getRefreshToken(userId);
    if (current && current !== usedRefresh) return findAccount(userId)?.access ?? null;
    if (!current) return null;
    if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, OTHER_TAB_WAIT_MS));
  }
  return null;
}

async function requestNewAccess(userId: string, baseURL: string): Promise<string | null> {
  const refresh = getRefreshToken(userId);
  if (!refresh) return null;
  try {
    // Чистый axios, а не apiClient: перехватчик 401 не должен ловить сам refresh.
    const res = await axios.post<RefreshResponse>(`${baseURL}${REFRESH_URL}`, { refresh });
    if (!res.data?.access) return null;
    updateAuthTokens(res.data.access, res.data.refresh, userId);
    return res.data.access;
  } catch {
    return rotatedByOtherTab(userId, refresh);
  }
}

/** Новый access-токен аккаунта или null (refresh нет, просрочен или отозван). Один запрос на аккаунт. */
export function refreshAccessToken(userId: string, baseURL = ''): Promise<string | null> {
  let current = pending.get(userId);
  if (!current) {
    current = requestNewAccess(userId, baseURL).finally(() => {
      pending.delete(userId);
    });
    pending.set(userId, current);
  }
  return current;
}
